import {
  CodeBlock,
  DiffBlock,
  LineNumberedBlock,
} from '@/components/ui/CodePreview';

import {
  fileReadFooter,
  parseFileRead,
  parseToolResult,
} from './tool-result-parser';

import type { ToolDescriptor } from './tool-descriptor';
import type { ReactNode } from 'react';

interface ToolPayloadPreviewProps {
  descriptor: ToolDescriptor;
  readRequestedLimit?: number;
  /** Rendered when the descriptor has nothing to preview (pending
   * approval, unsupported kind, empty args). */
  fallback?: ReactNode;
}

function getString(args: Record<string, unknown>, key: string): string {
  const raw = args[key];
  return typeof raw === 'string' ? raw : '';
}

function getNumber(
  args: Record<string, unknown>,
  key: string
): number | undefined {
  const raw = args[key];
  return typeof raw === 'number' ? raw : undefined;
}

// eslint-disable-next-line react-refresh/only-export-components
export function toolPayloadHasContent(descriptor: ToolDescriptor): boolean {
  switch (descriptor.kind) {
    case 'write':
      return getString(descriptor.args, 'content').length > 0;
    case 'edit':
      return (
        getString(descriptor.args, 'old_string').length > 0 ||
        getString(descriptor.args, 'new_string').length > 0
      );
    case 'read':
    case 'shell':
      return (
        parseToolResult(descriptor.result?.text ?? '', { trimEnd: true })
          .cleanText.length > 0
      );
    case 'other':
    default:
      return false;
  }
}

export function ToolPayloadPreview({
  descriptor,
  readRequestedLimit,
  fallback,
}: ToolPayloadPreviewProps) {
  const empty = fallback ?? null;
  if (!toolPayloadHasContent(descriptor)) return empty;

  switch (descriptor.kind) {
    case 'write':
      return (
        <CodeBlock text={getString(descriptor.args, 'content')} tone="subtle" />
      );

    case 'edit':
      return (
        <DiffBlock
          oldStr={getString(descriptor.args, 'old_string')}
          newStr={getString(descriptor.args, 'new_string')}
        />
      );

    case 'read': {
      if (!descriptor.result) return empty;
      if (descriptor.result.isError) {
        const parsed = parseToolResult(descriptor.result.text, {
          trimEnd: true,
        });
        return (
          <CodeBlock text={parsed.cleanText} tone="default" maxLines={40} />
        );
      }
      const parsed = parseFileRead(descriptor.result.text);
      if (!parsed.hasNumberedLines) {
        return (
          <CodeBlock text={parsed.cleanText} tone="subtle" maxLines={40} />
        );
      }
      const limit = readRequestedLimit ?? getNumber(descriptor.args, 'limit');
      return (
        <LineNumberedBlock
          lines={parsed.lines}
          footer={fileReadFooter(parsed, limit)}
        />
      );
    }

    case 'shell': {
      // Output only — callers render the command separately (chip / approval body).
      if (!descriptor.result) return empty;
      const parsed = parseToolResult(descriptor.result.text, { trimEnd: true });
      return (
        <CodeBlock
          text={parsed.cleanText}
          tone={descriptor.result.isError ? 'default' : 'subtle'}
          maxLines={40}
        />
      );
    }

    case 'other':
    default:
      return empty;
  }
}
